import { useState } from "react";
import { Bot, Check, ChevronDown, Copy } from "lucide-react";
import { buildEaIntegrationPrompt } from "../lib/eaPrompt";
import { useEaIntegrationKey } from "../hooks/useDashboardData";

async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);
  // Older browsers and non-HTTPS origins have no clipboard API - fall back to a selection copy.
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const ok = document.execCommand("copy");
  textarea.remove();
  if (!ok) throw new Error("copy failed");
}

/** A ready-made prompt for an AI to add dashboard reporting to a new EA. */
export function EaPromptCard({ apiBaseUrl }: { apiBaseUrl: string }) {
  const keyQuery = useEaIntegrationKey();
  const apiKey = keyQuery.data?.apiKey;
  const prompt = buildEaIntegrationPrompt(apiBaseUrl, apiKey);
  // The preview is on screen; only the copied text carries the real key.
  const preview = apiKey ? prompt.split(apiKey).join("�".repeat(12)) : prompt;
  const [copied, setCopied] = useState<"yes" | "failed" | null>(null);
  const [expanded, setExpanded] = useState(false);

  async function handleCopy() {
    try {
      await copyText(prompt);
      setCopied("yes");
    } catch {
      setCopied("failed");
    }
    setTimeout(() => setCopied(null), 2000);
  }

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Bot className="h-4 w-4 text-emerald-400" />
          <h3 className="text-sm font-semibold text-slate-100">AI prompt: connect a new EA</h3>
        </div>
        <button
          onClick={handleCopy}
          disabled={keyQuery.isLoading}
          className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-1.5 text-sm font-semibold text-slate-950 transition-colors hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {copied === "yes" ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied === "yes" ? "Copied" : copied === "failed" ? "Copy failed" : "Copy prompt"}
        </button>
      </div>
      <p className="text-sm text-slate-500">
        Paste this into an AI along with your EA's code. It says exactly which JSON to send to this
        dashboard and how, so the AI can add the reporting for you.{" "}
        {apiKey
          ? "It includes your EA API key (hidden in the preview), so the EA is ready to report once it's built. Only paste it into AI tools you trust, and don't give the compiled EA to anyone you wouldn't give the key to."
          : "It doesn't include the API key; you paste that into the EA's inputs yourself."}
      </p>

      <button
        onClick={() => setExpanded((v) => !v)}
        className="mt-3 flex items-center gap-1 text-sm font-medium text-slate-400 hover:text-slate-200"
      >
        <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
        {expanded ? "Hide prompt" : "Show prompt"}
      </button>
      {expanded && (
        <pre className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-slate-800 bg-slate-950 p-4 text-xs leading-relaxed text-slate-300">
          {preview}
        </pre>
      )}
    </div>
  );
}
